import { useNavigate } from 'react-router-dom';
import { userAppContext } from '../../context/Context';
import { useAcademicApplication } from '../../hooks/useAcademicApplication';
import { useCheckoutFlow } from '../../hooks/useCheckoutFlow';
import Tooltip from '../UI/Tooltip';
import { UserContext } from '../../types';
import { PricingCardProps, ButtonConfig } from './types';

export function PricingCard({
  title,
  info,
  price,
  priceMo,
  included,
  description,
  onAcademicApply,
  isCommercial: isCommercialProp,
}: PricingCardProps) {
  const navigate = useNavigate();
  const { groups, user, isAuthenticated } = userAppContext() as unknown as UserContext;
  const {
    trialType,
    userType,
    canSeePrices: canSeePricesFromHook,
    isCommercial: isCommercialFromHook,
  } = useAcademicApplication(user?.email, isAuthenticated);
  const { redirectToCheckout } = useCheckoutFlow();

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademicPlan = groups?.includes('ROLE_ACADEMIC');
  const isStandardPlan = groups?.includes('ROLE_STANDARD');
  const isAdvancedPlan = groups?.includes('ROLE_PROFESSIONAL');

  // Use tag-based logic from hook
  const isCommercial = isCommercialProp ?? (isTrial ? trialType === 'commercial' : isCommercialFromHook());
  const canSeePrices = canSeePricesFromHook();

  // For authenticated Standard/Academic users without pricing access, treat as commercial
  const shouldShowContactUs = isAuthenticated && (isStandardPlan || isAcademicPlan) && !canSeePrices;

  const isCurrentPlan =
    (title === 'Academic' && isAcademicPlan) ||
    (title === 'Standard' && isStandardPlan) ||
    (title === 'Advanced' && isAdvancedPlan);

  const getButtonConfig = (): ButtonConfig => {
    if (!isAuthenticated) {
      if (title === 'Standard' || title === 'Premium') {
        return { show: true, text: 'Contact Us' };
      }
      return { show: true, text: 'Register' };
    }

    if (isCurrentPlan) {
      return { showTag: true };
    }

    // Users not in allowed lists should contact us
    if (shouldShowContactUs) {
      return { show: true, text: 'Contact Us' };
    }

    if (isCommercial) {
      return { show: true, text: 'Contact Us' };
    }

    if (isTrial) {
      if (title === 'Academic') {
        return { show: true, text: 'Apply for Free Academic Plan', isAcademicApply: true };
      }
      if (title === 'Standard' || title === 'Advanced') {
        return {
          show: true,
          text: `Get ${title}`,
          disabled: true,
          tooltip: 'Apply for the Free Academic Plan to access paid plans',
        };
      }
      return { show: true, text: 'Contact Us' };
    }

    if (isAcademicPlan && (title === 'Standard' || title === 'Advanced')) {
      return { show: true, text: `Get ${title}`, isStripeUpgrade: true };
    }

    if (isStandardPlan) {
      if (title === 'Academic')
        return { show: true, text: 'Apply for Free Academic Plan', isAcademicApply: true };
      if (title === 'Advanced') return { show: true, text: 'Get Advanced', isStripeUpgrade: true };
    }

    if (isAdvancedPlan) {
      if (title === 'Academic')
        return { show: true, text: 'Apply for Free Academic Plan', isAcademicApply: true };
      if (title === 'Standard') return { show: true, text: 'Get Standard', isStripeUpgrade: true };
    }

    if (title === 'Premium') {
      return { show: true, text: 'Contact Us' };
    }

    return { show: true, text: 'Register' };
  };

  const buttonConfig = getButtonConfig();

  const handleButtonClick = () => {
    // If button says "Contact Us", always go to Support
    if (buttonConfig.text === 'Contact Us' || isCommercial || shouldShowContactUs) {
      navigate('/Support');
      return;
    }

    if (buttonConfig.isAcademicApply) {
      onAcademicApply();
    } else if (buttonConfig.isStripeUpgrade) {
      const priceId =
        title === 'Standard'
          ? process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID || 'price_1SCebx2Ml6stFA0rHcXCPwNj'
          : process.env.REACT_APP_STRIPE_ADVANCED_PRICE_ID || 'price_1SPPcK2Ml6stFA0rOiXzsUUM';
      redirectToCheckout({ title, priceId });
    } else {
      if (title === 'Standard' || title === 'Premium') {
        navigate('/Support');
      } else {
        const routes: Record<string, string> = {
          Academic: '/academic-apply',
          Advanced: '/free-trial-apply',
        };
        navigate(routes[title] || '/Register');
      }
    }
  };

  const renderButton = () => {
    if (buttonConfig.showTag) {
      return (
        <button
          className="card-btn"
          disabled
          style={{ backgroundColor: '#9e9e9e', cursor: 'default', width: '100%' }}
        >
          Current Plan
        </button>
      );
    }

    if (!buttonConfig.show) return null;

    const buttonElement = (
      <button
        className={`card-btn ${title === 'Advanced' ? 'card-btn-advanced' : ''}`}
        onClick={handleButtonClick}
        disabled={buttonConfig.disabled}
        style={{
          ...(buttonConfig.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
          width: '100%',
        }}
      >
        {isCommercial || shouldShowContactUs ? 'Contact us' : buttonConfig.text}
      </button>
    );

    return buttonConfig.tooltip ? (
      <Tooltip content={buttonConfig.tooltip} placement="top" allowCopy={false}>
        {buttonElement}
      </Tooltip>
    ) : (
      buttonElement
    );
  };

  const showPrices =
    price && isAuthenticated && !isCommercial && !isTrial && canSeePrices && !shouldShowContactUs;
  const showTrialMessage = isTrial && !isCommercial && (title === 'Standard' || title === 'Advanced');
  const showContactForPrice =
    ((!isAuthenticated || (!showPrices && !showTrialMessage)) && title !== 'Academic') || shouldShowContactUs;
  const showAcademicFree = title === 'Academic' && (!showPrices || !isAuthenticated);

  return (
    <div
      className={`PricingCard ${title === 'Advanced' ? 'PricingCard-advanced border-2 border-solid' : ''} ${title === 'Premium' ? 'PricingCard-premium' : ''}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {info && (
        <div
          style={{
            position: 'absolute',
            top: '25px',
            right: '-40px',
            backgroundColor: '#667eea',
            color: 'white',
            padding: '5px 40px',
            transform: 'rotate(45deg)',
            fontSize: '12px',
            fontWeight: 'bold',
            zIndex: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          Most Popular
        </div>
      )}

      {/* Title */}
      <div>
        <p className="font-medium text-2xl">{title}</p>
        <hr className="h-1" />
      </div>

      {/* Price */}
      <div>
        {showPrices && (
          <>
            <div className="h-12 grid items-center justify-center">
              {title === 'Academic' ? (
                <div>
                  <span className="font-bold text-2xl">{price}</span>
                </div>
              ) : priceMo ? (
                <div>
                  <span className="font-bold text-2xl">{priceMo}</span>
                  <small>€/mo</small>
                </div>
              ) : null}
              {title !== 'Academic' && (
                <span>
                  <small>
                    ({price}
                    {price !== 'Free' ? '€ billed yearly' : ''})
                  </small>
                </span>
              )}
            </div>
          </>
        )}
        {showAcademicFree && (
          <div className="h-12 grid items-center justify-center">
            <div>
              <span className="font-bold text-2xl">Free</span>
            </div>
          </div>
        )}
        {showTrialMessage && (
          <div className="h-12 grid items-center justify-center">
            <p className="text-xl font-semibold">Requires academic approval</p>
          </div>
        )}
        {showContactForPrice && (
          <div className="h-12 grid items-center justify-center">
            <p className="text-xl font-semibold" style={{ opacity: 0.8 }}>
              Contact us for pricing
            </p>
          </div>
        )}
        <hr className="h-1" />
      </div>

      {/* Text */}
      <div style={{ minHeight: '60px' }}>
        <p className="card-price">{included}</p>
      </div>

      {/* Text */}
      <div style={{ minHeight: '80px' }}>
        {description && (
          <>
            <hr className="h-1 my-3" />
            <p
              className="card-price"
              style={{ fontSize: '0.9em' }}
              dangerouslySetInnerHTML={{
                __html: description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
              }}
            />
          </>
        )}
      </div>

      {/* Button */}
      <div style={{ marginTop: 'auto', paddingTop: '20px' }}>{renderButton()}</div>
    </div>
  );
}
